import { useState, memo } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import FormControl from '@zenbusiness/zui/FormControl';
import FormControlLabel from '@zenbusiness/zui/FormControlLabel';
import Radio from '@zenbusiness/zui/Radio';
import RadioGroup from '@zenbusiness/zui/RadioGroup';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import TextField from '@zenbusiness/zui/TextField';
import PropTypes from 'prop-types';

const RegulatedStatusUIField = ({
  field = { setSaved: () => {}, setLoading: () => {} },
  dataChangeHandler = () => {},
  disabled,
  data
}) => {
  const [selectedOption, setSelectedOption] = useState(data?.isRegulated);
  const [showRegulatorNameError, setShowRegulatorNameError] = useState(false);
  const trackRegulatedStatusChangeClick = useButtonPressedCallback({
    elementName: 'banking_collect_info',
    appPageName: 'banking_application'
  });

  const onDataChanged = (event, option, isRegulated = selectedOption) => {
    trackRegulatedStatusChangeClick(event);
    field.setLoading();
    // The field is "complete" only when the user picked No, or when they picked Yes
    // AND a non-empty regulator name was supplied. The dataChangeHandler always reports
    // isSave: true on a successful persist, so we AND it with isComplete to keep the
    // submit gate closed for transient states like Yes+empty-name.
    const isComplete = isRegulated === false || (isRegulated === true && Boolean(option));
    dataChangeHandler(
      true,
      { isRegulated, regulatorName: option },
      (forceUpdate, { isSave, isError }) => {
        field.setSaved(isSave && isComplete, isError);
        forceUpdate();
      },
      field.key
    );
  };

  const handleChangeRadioOption = (event) => {
    const value = event.target.value === 'true';
    setSelectedOption(value);
    // Persist immediately on every radio change so the server state matches the UI.
    // For "Yes", we pass the previously-saved regulatorName (or null) so onDataChanged's
    // isComplete check keeps the field unsaved until a name is provided.
    const nextName = value === false ? null : (data?.regulatorName ?? null);
    onDataChanged(event, nextName, value);
  };

  const handleRegulatorNameBlur = (event) => {
    const name = event.target.value?.trim();
    setShowRegulatorNameError(!name);
    // Coerce empty/whitespace to null so a cleared field overwrites any prior saved name.
    onDataChanged(event, name || null, true);
  };

  const handleRegulatorNameChange = (event) => {
    if (showRegulatorNameError && event.target.value.trim()) {
      setShowRegulatorNameError(false);
    }
  };

  return (
    <FormControl
      fullWidth
      sx={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 3 }}
      variant="outlined"
    >
      <RadioGroup
        aria-labelledby="demo-radio-buttons-group-label"
        name="regulated-status-radio-group"
        onChange={handleChangeRadioOption}
        value={selectedOption}
      >
        <FormControlLabel
          control={
            <Radio
              color="info"
              disabled={disabled}
              size="medium"
            />
          }
          label="Yes"
          value
        />
        <FormControlLabel
          control={
            <Radio
              color="info"
              disabled={disabled}
              size="medium"
            />
          }
          label="No"
          value={false}
        />
      </RadioGroup>

      {selectedOption === true && (
        <Stack spacing={1.5}>
          <Text
            color="tertiary"
            size="md"
          >
            Please provide the name of your regulator.
          </Text>

          <TextField
            defaultValue={data?.regulatorName}
            disabled={disabled}
            error={showRegulatorNameError}
            helperText={showRegulatorNameError ? 'Regulator name is required.' : ''}
            id="regulatorName"
            label="Enter regulator name"
            onBlur={handleRegulatorNameBlur}
            onChange={handleRegulatorNameChange}
            type="text"
          />
        </Stack>
      )}
    </FormControl>
  );
};

RegulatedStatusUIField.propTypes = {
  data: PropTypes.shape({
    isRegulated: PropTypes.bool,
    regulatorName: PropTypes.string
  }),
  dataChangeHandler: PropTypes.func,
  field: PropTypes.shape({
    setSaved: PropTypes.func,
    setLoading: PropTypes.func,
    key: PropTypes.string
  }),
  disabled: PropTypes.bool
};

function areEqual(preProp, nextProp) {
  return (
    preProp.field === nextProp.field &&
    preProp.data === nextProp.data &&
    preProp.disabled === nextProp.disabled
  );
}

export default memo(RegulatedStatusUIField, areEqual);
